import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, SeverityPill, ProgressBar, fmt$, fmtN } from "@/components/primitives";
import EmptyStateCard from "@/components/EmptyStateCard";

const OWN_LABEL = { rented: "rented", financed: "financed", owned: "owned", unknown: "unknown", contracted: "contracted", mixed: "mixed" };
const LEAK = new Set(["contracted", "rented", "financed", "mixed"]);
const CHIPS = [
  ["needs_committed", "Needs committed volume"],
  ["needs_ownership", "Needs ownership"],
  ["flagged", "Flagged"],
  ["winback", "Win-back"],
  ["all", "All"],
];
const PAGE = 30;

export default function Accounts() {
  const nav = useNavigate();
  const [chip, setChip] = useState("needs_committed");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [drafts, setDrafts] = useState({});
  const [saved, setSaved] = useState({});
  const inputRefs = useRef({});

  const { data, refetch } = useQuery({
    queryKey: ["accounts", "everything"],
    queryFn: () => endpoints.accounts({ track: "everything" }),
  });
  const all = data?.accounts || [];
  const total = data?.totalAll ?? all.length;
  const noData = data && total === 0;

  const progress = useMemo(() => {
    const committed = all.filter((r) => r.committedKgPerPeriod != null).length;
    const own = {};
    for (const r of all) { const k = r.ownership || "unknown"; own[k] = (own[k] || 0) + 1; }
    return { committed, own };
  }, [all]);

  const filtered = useMemo(() => {
    let r = all;
    if (chip === "needs_committed") r = r.filter((x) => LEAK.has(x.ownership) && x.committedKgPerPeriod == null);
    else if (chip === "needs_ownership") r = r.filter((x) => x.ownership === "unknown" || x.ownershipSource !== "simpro");
    else if (chip === "flagged") r = r.filter((x) => x.flagged);
    else if (chip === "winback") r = r.filter((x) => x.winBack);
    if (query.trim()) { const q = query.trim().toLowerCase(); r = r.filter((x) => (x.name || "").toLowerCase().includes(q)); }
    return [...r].sort((a, b) => (b.spend12m || 0) - (a.spend12m || 0));
  }, [all, chip, query]);

  const visible = filtered.slice(0, limit);

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  const flashSaved = (id) => { setSaved((s) => ({ ...s, [id]: true })); setTimeout(() => setSaved((s) => { const n = { ...s }; delete n[id]; return n; }), 1800); };

  const saveField = async (r, field, rawVal) => {
    const cur = field === "committed" ? (r.committedKgPerPeriod ?? "") : (r.ownershipType || "");
    if (String(rawVal) === String(cur)) return; // no change
    const body = { commitmentPeriod: r.commitmentPeriod || "monthly" };
    if (field === "committed") body.committedKgPerPeriod = rawVal === "" ? null : parseFloat(rawVal);
    if (field === "ownership") body.ownershipType = rawVal || null;
    await endpoints.saveAccountSettings(r.customerId, body);
    setDrafts((x) => { const n = { ...x }; if (n[r.id]) delete n[r.id][field]; return n; });
    flashSaved(r.id); refetch();
  };

  const onCommittedKeyDown = (e, idx) => {
    if (e.key === "Tab" && !e.shiftKey) {
      const next = inputRefs.current[idx + 1];
      if (next) { e.preventDefault(); next.focus(); next.select?.(); }
    }
  };

  const inp = { width: 74, background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 6, padding: "6px 8px", color: "var(--ui-text-hi)", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13 };
  const sel = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 6, padding: "6px 8px", color: "var(--ui-text-hi)", fontFamily: "'Inter',sans-serif", fontSize: 12.5, width: 172 };
  const colW = { account: 220, committed: 150, ownership: 190, signal: 150, spend: 110, action: 60 };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>Accounts</div>
        <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>Set committed volume &amp; ownership — the commercial layer behind every leak figure</div>
      </div>

      {noData ? (
        <Card style={{ padding: 0 }}>
          <EmptyStateCard testid="accounts-empty" style={{ border: "none", boxShadow: "none", background: "transparent" }}
            message="No data yet — run a sync to pull your Simpro history. Your accounts, spend and ownership will appear here once synced." />
        </Card>
      ) : (
      <>
        {/* PROGRESS HEADER */}
        <Card style={{ padding: "20px 24px", marginBottom: 16 }}>
          <div className="flex items-center" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>
              {progress.committed} of {total} accounts have a committed volume
            </div>
            <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)" }}>
              {["contracted", "rented", "financed", "mixed", "owned", "unknown"].filter((k) => progress.own[k]).map((k) => `${progress.own[k]} ${k}`).join(" · ")}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <ProgressBar pct={total ? (progress.committed / total) * 100 : 0} severity="good" />
          </div>
        </Card>

        <Card style={{ padding: 0 }}>
          {/* CONTROLS */}
          <div className="flex items-center" style={{ justifyContent: "space-between", gap: 12, padding: "18px 24px", flexWrap: "wrap" }}>
            <div className="flex items-center" style={{ gap: 6, flexWrap: "wrap" }}>
              {CHIPS.map(([k, label]) => {
                const on = chip === k;
                return (
                  <button key={k} data-testid={`acc-chip-${k}`} onClick={() => { setChip(k); setLimit(PAGE); }} className="prose-i"
                    style={{ fontSize: 12.5, borderRadius: 8, padding: "7px 12px", cursor: "pointer",
                      border: `1px solid ${on ? "var(--ac-indigo)" : "var(--ui-line)"}`,
                      background: on ? "var(--ac-indigo)" : "transparent",
                      color: on ? "var(--ui-on-accent)" : "var(--ui-text-mid)" }}>{label}</button>
                );
              })}
            </div>
            <div className="flex items-center" style={{ gap: 10 }}>
              {/* §2 zero-spend entry point → the Sites engine */}
              <button data-testid="accounts-zero-spend" onClick={() => nav("/sites?filter=dry")} className="prose-i"
                style={{ fontSize: 12.5, borderRadius: 8, padding: "8px 12px", cursor: "pointer", whiteSpace: "nowrap",
                  border: "1px solid var(--sv-crit)", background: "var(--sv-crit-bg)", color: "var(--sv-crit)" }}>
                Zero-spend sites →
              </button>
              <input data-testid="accounts-search" placeholder="Search account…" value={query}
                onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }} className="prose-i"
                style={{ background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 10, padding: "9px 14px", color: "var(--ui-text-hi)", fontSize: 14, width: 220 }} />
            </div>
          </div>

          {chip === "needs_committed" && (
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", padding: "0 24px 14px" }}>
              Contracted accounts with no committed volume, highest 12-month spend first — the top-value work.
            </div>
          )}

          {/* HEADER */}
          <div style={{ display: "flex", alignItems: "center", background: "var(--ui-bg)",
            borderTop: "1px solid var(--ui-line-soft)", borderBottom: "1px solid var(--ui-line-soft)", padding: "10px 26px" }}>
            <div className="uppercase-label" style={{ width: colW.account }}>Account</div>
            <div className="uppercase-label" style={{ width: colW.committed }}>Committed / mo</div>
            <div className="uppercase-label" style={{ width: colW.ownership }}>Ownership</div>
            <div className="uppercase-label" style={{ width: colW.signal }}>Signal</div>
            <div className="uppercase-label" style={{ width: colW.spend }}>12mo spend</div>
            <div style={{ width: colW.action }} />
          </div>

          <div style={{ padding: "0 26px" }}>
            {visible.length === 0 && (
              <div className="prose-i" data-testid="accounts-no-match" style={{ padding: 24, color: "var(--ui-text-lo)" }}>No accounts match this filter.</div>
            )}
            {visible.map((r, i) => {
              const d = drafts[r.id] || {};
              const committedVal = d.committed !== undefined ? d.committed : (r.committedKgPerPeriod ?? "");
              const ownVal = d.ownership !== undefined ? d.ownership : (r.ownershipType || "");
              const ownFrom = r.ownershipSource === "simpro" ? "Simpro" : "Derived";
              return (
                <div key={r.id} className="flex items-center" style={{ padding: "13px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
                  <div style={{ width: colW.account }}>
                    <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>{r.name}</div>
                    <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 2 }}>
                      {r.siteCount} {r.siteCount === 1 ? "site" : "sites"} · {OWN_LABEL[r.ownership] || r.ownership}
                    </div>
                  </div>
                  <div style={{ width: colW.committed }}>
                    <div className="flex items-center" style={{ gap: 5 }}>
                      <input type="number" value={committedVal} placeholder="—"
                        ref={(el) => { inputRefs.current[i] = el; }}
                        data-testid={`bulk-committed-${r.customerId}`}
                        onChange={(e) => setDraft(r.id, { committed: e.target.value })}
                        onBlur={(e) => saveField(r, "committed", e.target.value)}
                        onKeyDown={(e) => onCommittedKeyDown(e, i)}
                        style={inp} />
                      <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>kg</span>
                    </div>
                  </div>
                  <div style={{ width: colW.ownership }}>
                    <select value={ownVal} onChange={(e) => { setDraft(r.id, { ownership: e.target.value }); saveField(r, "ownership", e.target.value); }} style={sel}>
                      <option value="">{ownFrom} ({r.ownershipResolved})</option>
                      <option value="rented">Rented</option>
                      <option value="financed">Financed</option>
                      <option value="sold">Owned outright</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div style={{ width: colW.signal }}>
                    {r.flagged ? <SeverityPill severity={r.severity} label={r.typeLabel} />
                      : r.dataState === "insufficient_history"
                        ? <span data-testid={`state-insufficient-${r.customerId}`} className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-mid)", border: "1px solid var(--ui-line)", background: "var(--ui-bg)", borderRadius: 8, padding: "3px 9px" }}>Insufficient history</span>
                        : r.dataState === "no_data"
                          ? <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", border: "1px solid var(--ui-line)", background: "var(--ui-bg)", borderRadius: 8, padding: "3px 9px" }}>No orders yet</span>
                          : r.winBack
                            ? <span className="prose-i" style={{ fontSize: 11, fontWeight: 600, color: "var(--sv-watch)", background: "var(--sv-watch-bg)", border: "1px solid var(--sv-watch)", borderRadius: 8, padding: "3px 9px" }}>Win-back</span>
                            : <SeverityPill severity="good" label="Healthy" />}
                  </div>
                  <div style={{ width: colW.spend }}>
                    <span className="num" style={{ fontSize: 15, color: "var(--ui-text-hi)" }}>{fmt$(r.spend12m)}</span>
                  </div>
                  <div style={{ width: colW.action, display: "flex", justifyContent: "flex-end" }}>
                    {saved[r.id]
                      ? <span data-testid={`saved-${r.customerId}`} className="prose-i" style={{ fontSize: 12, color: "var(--sv-good)" }}>Saved ✓</span>
                      : <Button variant="secondary" onClick={() => nav(`/accounts/${r.customerId}`)} style={{ padding: "7px 12px", fontSize: 12 }}>Open</Button>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* PAGINATION */}
          {filtered.length > 0 && (
            <div className="flex items-center" style={{ justifyContent: "space-between", padding: "16px 26px", borderTop: "1px solid var(--ui-line-soft)" }}>
              <span className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-lo)" }}>Showing {Math.min(limit, filtered.length)} of {filtered.length}{chip !== "all" ? ` (of ${total} total)` : ""}</span>
              {limit < filtered.length && <Button variant="secondary" onClick={() => setLimit(limit + PAGE)} style={{ fontSize: 13 }}>Load {Math.min(PAGE, filtered.length - limit)} more</Button>}
            </div>
          )}
        </Card>
      </>
      )}
    </div>
  );
}
